import type {
  CreateWebhookEndpointRequest,
  UpdateWebhookEndpointRequest,
} from '@compass/client/types/webhook';
import { webhookService } from '@services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/services/query.config';

/**
 * Endpoint queries and mutations for the customer Webhooks section.
 *
 * Every hook takes `enabled` from the caller's permission map: a 403 never
 * settles in this app's transport, so a denied request must never be sent.
 * Mutations refresh everything for the org on settle — a failed call (an
 * endpoint changed or deleted elsewhere) must still drop stale rows.
 */

export function useWebhookEndpoints(organizationId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.webhooks.endpoints(organizationId),
    queryFn: () => webhookService.listWebhookEndpoints({ organization_id: organizationId }),
    enabled: enabled && !!organizationId,
    staleTime: 30 * 1000,
  });
}

export function useWebhookEndpoint(organizationId: string, endpointId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.webhooks.endpoint(organizationId, endpointId),
    // The response wraps the endpoint ({endpoint}); unwrap it here so callers
    // see a WebhookEndpoint. React Query rejects undefined data, hence null.
    queryFn: async () =>
      (
        await webhookService.getWebhookEndpoint({
          organization_id: organizationId,
          endpoint_id: endpointId,
        })
      ).endpoint ?? null,
    enabled: enabled && !!organizationId && !!endpointId,
    staleTime: 30 * 1000,
  });
}

function useInvalidateWebhooks(organizationId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.org(organizationId) });
}

export function useCreateWebhookEndpoint(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: (request: CreateWebhookEndpointRequest) =>
      webhookService.createWebhookEndpoint(request),
    // The result carries the one-time signing secret: do not keep it in the
    // mutation cache once the dialog that shows it has taken it.
    gcTime: 0,
    onSettled: invalidate,
  });
}

export function useUpdateWebhookEndpoint(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: async (request: UpdateWebhookEndpointRequest) =>
      (await webhookService.updateWebhookEndpoint(request)).endpoint,
    // The variables carry plaintext custom header values; see
    // useCreateWebhookEndpoint.
    gcTime: 0,
    onSettled: invalidate,
  });
}

/** Status changes and delete share one shape: org + endpoint id in the path, no body. */
export type EndpointStatusAction = 'pause' | 'resume' | 'disable' | 'delete';

interface StatusActionResult {
  dispatcherError: boolean;
  /** Pause/disable: pending deliveries cancelled at the dispatcher. */
  cancelledDeliveries: number;
  /** Resume from paused: the replay of what was missed while paused. */
  replayOperationId?: string;
}

interface StatusActionResponse {
  dispatcher_error?: boolean;
  cancelled_deliveries?: number;
  replay_operation_id?: string;
}

type EndpointRequest = { organization_id: string; endpoint_id: string };

const STATUS_CALLS: Record<
  EndpointStatusAction,
  (request: EndpointRequest) => Promise<StatusActionResponse | null | undefined>
> = {
  pause: request => webhookService.pauseWebhookEndpoint(request),
  resume: request => webhookService.resumeWebhookEndpoint(request),
  disable: request => webhookService.disableWebhookEndpoint(request),
  delete: request => webhookService.deleteWebhookEndpoint(request),
};

function toStatusActionResult(res: StatusActionResponse | null | undefined): StatusActionResult {
  return {
    dispatcherError: res?.dispatcher_error ?? false,
    cancelledDeliveries: res?.cancelled_deliveries ?? 0,
    replayOperationId: res?.replay_operation_id || undefined,
  };
}

export function useEndpointStatusAction(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: async ({
      action,
      endpointId,
    }: {
      action: EndpointStatusAction;
      endpointId: string;
    }): Promise<StatusActionResult> =>
      toStatusActionResult(
        await STATUS_CALLS[action]({ organization_id: organizationId, endpoint_id: endpointId }),
      ),
    onSettled: invalidate,
  });
}

export function useRotateWebhookSecret(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: (endpointId: string) =>
      webhookService.rotateWebhookEndpointSecret({
        organization_id: organizationId,
        endpoint_id: endpointId,
      }),
    // Carries the new signing secret; see useCreateWebhookEndpoint.
    gcTime: 0,
    onSettled: invalidate,
  });
}

export function useSendWebhookTestEvent(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: (endpointId: string) =>
      webhookService.sendWebhookTestEvent({
        organization_id: organizationId,
        endpoint_id: endpointId,
      }),
    onSettled: invalidate,
  });
}
